import { ReactNode, useEffect, useState } from "react";

import { TextT } from "@phosphor-icons/react";
import { fonts } from "@reactive-resume/utils";
import { useCoverLetterStore } from "@/client/stores/cover-letter";
import type { ComboboxOption } from "@reactive-resume/ui";
import { Combobox, Label, Slider, Switch } from "@reactive-resume/ui";

import { CLCollapsibleSection } from "../../../CollapsibleSection";

const localFonts = ["Arial", "Cambria", "Garamond", "Times New Roman"];

const families = fonts.map((font) => ({
  value: font.family,
  label: <span style={{ fontFamily: font.family }}>{font.family}</span>,
})) satisfies ComboboxOption[];

families.push(
  ...localFonts.map((font) => ({
    value: font,
    label: <span style={{ fontFamily: font }}>{font}</span>,
  })),
);

export const CLTypographySection = () => {
  const [subsets, setSubsets] = useState<ComboboxOption[]>([]);
  const [variants, setVariants] = useState<ComboboxOption[]>([]);

  const setValue = useCoverLetterStore((state) => state.setValue);
  const typography = useCoverLetterStore(
    (state) => state.coverLetter.data.metadata.typography,
  );

  useEffect(() => {
    const fontSubsets =
      fonts.find((font) => font.family === typography.font.family)?.subsets ?? [];
    if (fontSubsets.length > 0) {
      setSubsets(fontSubsets.map((subset) => ({ value: subset, label: subset })));
    }

    const fontVariants =
      fonts.find((font) => font.family === typography.font.family)?.variants ?? [];
    if (fontVariants.length > 0) {
      setVariants(fontVariants.map((variant) => ({ value: variant, label: variant })));
    }
  }, [typography.font.family]);

  const headerHTML: ReactNode = (
    <div className="flex items-center gap-x-4">
      <TextT size={18} />
      <h2 className="line-clamp-1 text-base font-bold text-gray-800 lg:text-lg">Typography</h2>
    </div>
  );

  const contentHTML: ReactNode = (
    <main className="grid gap-y-6">
      <div className="flex flex-col space-y-1.5">
        <Label>Font Family</Label>
        <div className="w-full">
          <Combobox
            options={families}
            value={typography.font.family}
            searchPlaceholder="Search for a font family"
            onValueChange={(selected) => {
              setValue("metadata.typography.font.family", selected);
              setValue("metadata.typography.font.subset", "latin");
              setValue("metadata.typography.font.variants", ["regular"]);
            }}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-x-4">
        <div className="space-y-1.5">
          <Label>Font Subset</Label>
          <Combobox
            options={subsets}
            value={typography.font.subset}
            searchPlaceholder="Search for a font subset"
            onValueChange={(value) => setValue("metadata.typography.font.subset", value)}
          />
        </div>

        <div className="space-y-1.5">
          <Label>Font Variants</Label>
          <Combobox
            multiple
            options={variants}
            value={typography.font.variants}
            searchPlaceholder="Search for a font variant"
            onValueChange={(value) => setValue("metadata.typography.font.variants", value)}
          />
        </div>
      </div>

      <div className="space-y-1.5">
        <Label>Font Size</Label>
        <div className="flex items-center gap-x-4 py-1">
          <Slider
            min={7}
            max={18}
            step={0.05}
            value={[typography.font.size]}
            onValueChange={(value) => setValue("metadata.typography.font.size", value[0])}
          />
          <span className="text-base font-bold">{typography.font.size}</span>
        </div>
      </div>

      <div className="space-y-1.5">
        <Label>Line Height</Label>
        <div className="flex items-center gap-x-4 py-1">
          <Slider
            min={0}
            max={3}
            step={0.05}
            value={[typography.lineHeight]}
            onValueChange={(value) => setValue("metadata.typography.lineHeight", value[0])}
          />
          <span className="text-base font-bold">{typography.lineHeight}</span>
        </div>
      </div>

      <div className="space-y-1.5">
        <Label>Options</Label>
        <div className="flex items-center gap-x-4 py-1">
          <Switch
            checked={typography.hideIcons}
            onCheckedChange={(checked) => setValue("metadata.typography.hideIcons", checked)}
          />
          <Label>Hide Icons</Label>
        </div>
        <div className="flex items-center gap-x-4 py-1">
          <Switch
            checked={typography.underlineLinks}
            onCheckedChange={(checked) =>
              setValue("metadata.typography.underlineLinks", checked)
            }
          />
          <Label>Underline Links</Label>
        </div>
      </div>
    </main>
  );

  return <CLCollapsibleSection id="cl-typography" header={headerHTML} content={contentHTML} />;
};
